import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import styled from '@emotion/styled';
import { Box, Card, CardContent, Typography } from '@mui/material';

import { TableColumnItem } from '@/components/table/B3Table';
import { currencyFormat, displayFormat } from '@/utils';
import { InvoiceList } from '@/pages/MultiInvoice/utils/types';

import B3Pulldown from './components/B3Pulldown';

export interface MultiInvoiceItemCardProps {
  item: any;
  checkBox?: (disable: boolean) => ReactElement;
  handleViewInvoice: (item: InvoiceList) => void;
  setIsRequestLoading: (bool: boolean) => void;
  handleGetCorrespondingCurrency: (code: string) => string;
  addBottom: boolean;
}

const StyleCheckoutContainer = styled(Box)(() => ({
  '& > span': {
    padding: '0 9px 0 0',
  },
}));

export function MultiInvoiceItemCard(props: MultiInvoiceItemCardProps) {
  const currentDate = new Date().getTime();
  const {
    item,
    checkBox,
    handleViewInvoice,
    setIsRequestLoading
  } = props;
  const b3Lang = useB3Lang();
  const navigate = useNavigate();

  const { id } = item;

  const columnAllItems: TableColumnItem<InvoiceList>[] = [
    {
      key: 'orderNumber',
      title: b3Lang('invoice.invoiceItemCardHeader.order'),
      render: () => (
        <Box
          sx={{
            color: '#000000',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => {
            navigate(`/orderDetail/${item.orderNumber}`);
          }}
        >
          {item?.orderNumber || '-'}
        </Box>
      ),
    },
    {
      key: 'createdAt',
      title: b3Lang('invoice.invoiceItemCardHeader.invoiceDate'),
      render: () => `${item.createdAt ? displayFormat(+item.InvoiceDate) : '–'}`,
    },
    {
      key: 'updatedAt',
      title: b3Lang('invoice.invoiceItemCardHeader.dueDate'),
      render: () => {
        const { InvoiceDueDate, status } = item;
        const isOverdue = currentDate > InvoiceDueDate * 1000 && status !== 2;

        return (
          <Typography
            sx={{
              color: isOverdue ? '#D32F2F' : 'rgba(0, 0, 0, 0.87)',
              fontSize: '14px',
            }}
          >
            {`${item.InvoiceDueDate ? displayFormat(+item.InvoiceDueDate) : '–'}`}
          </Typography>
        );
      },
    },
    {
      key: 'originalBalance',
      title: b3Lang('invoice.invoiceItemCardHeader.invoiceTotal'),
      render: () => {
        const { InvoiceTotal } = item;
        const originalAmount = +InvoiceTotal.value;

        return currencyFormat(originalAmount || 0);
      },
    },
    {
      key: 'openBalance',
      title: b3Lang('invoice.invoiceItemCardHeader.amountDue'),
      render: () => {
        const { openBalance } = item;

        const openAmount = +openBalance.value;

        return currencyFormat(openAmount || 0);
      },
    },
  ];

  return (
    <Card
      sx={{
        marginBottom: 0,
      }}
    >
      <CardContent
        sx={{
          color: 'rgba(0, 0, 0, 0.6)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: '0.5rem',
            }}
          >
            <StyleCheckoutContainer>
              {checkBox && checkBox(!!item?.disableCurrentCheckbox)}
            </StyleCheckoutContainer>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              <Box
                sx={{
                  color: '#000000',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => {
                  handleViewInvoice(item);
                }}
              >
                {id || '-'}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ mb: '0.5rem' }}>
            <B3Pulldown
              row={item}
              setIsRequestLoading={setIsRequestLoading}
            />
          </Box>
        </Box>

        {columnAllItems.map((list: CustomFieldItems) => (
          <Box
            key={list.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: '4px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                color: 'rgba(0, 0, 0, 0.87)',
                mr: '5px',
                whiteSpace: 'nowrap',
              }}
            >
              {`${list.title}:`}
            </Typography>
            <Box
              sx={{
                color: 'black',
                wordBreak: 'break-all',
              }}
            >
              {list?.render ? list.render() : item[list.key]}
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
